import { useCallback } from 'react'

import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'

import { billingOrgInvitesQueryKey } from '@/hooks/billing/types'
import { useIsAtLeastAdmin } from '@/hooks/billing/useBillingRole'

import type { PaginatedResponse } from '@/types/Api'
import { UUID } from '@/types/Id'

export interface OrganizationInvite {
  invitee_email: string
  expires_at: Date
}

interface UseBillingOrgInvitesArgs<T> {
  billingOrgId: UUID | ''
  select?: (
    invites: InfiniteData<
      PaginatedResponse<OrganizationInvite>,
      PaginateRequest
    >,
  ) => T
}

// PropelAuth does not follow our pagination conventions
// we need to use a custom type for the pagination request
type PaginateRequest = {
  page_size: number
  page_number: number
}

const fetchBillingOrgInvites = async (
  accessToken: string | undefined | null,
  billingOrgId: UUID | '',
  pagination: PaginateRequest,
): Promise<PaginatedResponse<OrganizationInvite>> => {
  if (!billingOrgId || accessToken === null || accessToken === undefined) {
    return { count: 0, has_more: false, data: [] }
  }

  const url =
    `${API_URL}/api/v1/invites/${billingOrgId}` +
    `?page_size=${pagination.page_size}` +
    `&page_number=${pagination.page_number}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new Error(res.statusText)
  }

  return convertDatesInObject(await res.json())
}

const getInvitesNextPageParam = (
  lastPage: PaginatedResponse<OrganizationInvite>,
  allPages: Array<PaginatedResponse<OrganizationInvite>>,
  lastPageParam: PaginateRequest,
  allPageParams: Array<PaginateRequest>,
): PaginateRequest | undefined => {
  if (!lastPage.has_more) {
    return undefined
  }
  return {
    page_size: lastPageParam.page_size,
    page_number: (lastPageParam.page_number ?? -1) + 1,
  }
}

export const useBillingOrgInvites = <T = OrganizationInvite[]>(
  args?: UseBillingOrgInvitesArgs<T>,
) => {
  const { accessToken } = useAuthInfo()
  const isAdmin = useIsAtLeastAdmin()

  const selector = useCallback(
    (
      data: InfiniteData<
        PaginatedResponse<OrganizationInvite>,
        PaginateRequest
      >,
    ) => {
      if (args?.select) {
        return args.select(data)
      }
      return data.pages.map((res) => res.data).flat() as unknown as T
    },
    [args?.select],
  )

  return useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      fetchBillingOrgInvites(accessToken, args?.billingOrgId || '', pageParam),
    queryKey: billingOrgInvitesQueryKey.all((args?.billingOrgId as UUID) || ''),
    initialPageParam: { page_size: 100, page_number: 0 } as PaginateRequest,
    getNextPageParam: getInvitesNextPageParam,
    enabled:
      accessToken !== undefined &&
      accessToken !== null &&
      !!args?.billingOrgId &&
      isAdmin,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: selector,
  })
}
